import { useState, useEffect } from "react";
import { MdClose, MdOutlinePeople, MdChevronRight } from "react-icons/md";
import { FaMoneyBillWave, FaWrench } from "react-icons/fa";
import tw from "tailwind-styled-components";
import { useNavigate } from "react-router-dom";

const PendingTasksModal = ({ onClose, pendingTasks }) => {
    const navigate = useNavigate();
    const [height, setHeight] = useState(window.innerHeight);


    const [tasks, setTasks] = useState({
        tenantActions: {
            moveInRequests: pendingTasks?.tenantActions?.moveInRequests || 0,
            moveInVerification: pendingTasks?.tenantActions?.moveInVerificationPending || 0,
            moveOutRequests: pendingTasks?.tenantActions?.moveOutRequests || 0,
            guestRequests: pendingTasks?.tenantActions?.guestRequests || 0
        },
        financeActions: {
            landlordPayments: pendingTasks?.financeActions?.landlordPaymentsPending || 0,
            refundRequests: pendingTasks?.financeActions?.refundRequests || 0,
            unpaidExpenses: pendingTasks?.financeActions?.unpaidExpences || 0,
        },
        operationsActions: {
            unassignedComplaints: pendingTasks?.operationActions?.unAssignedTenantComplaints || 0,
            agreementRenewals: pendingTasks?.operationActions?.agreementExpired || 0,
            unassignedPropComplaints: pendingTasks?.operationActions?.unAssignedPropComplaints || 0,
            unassignedLeads: pendingTasks?.operationActions?.unAssignedLeads || 0,
        }
    });

    useEffect(() => {
        const handleResize = () => {
            setHeight(window.innerHeight);
        };

        window.addEventListener('resize', handleResize);
        handleResize();

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    const handleTaskClick = (type, count, route) => {
        if (route) {
            navigate(route);
            onClose();
        }
    };

    const taskSections = [
        {
            title: "Tenant Actions",
            icon: <MdOutlinePeople className="w-4 h-4" />,
            color: "text-blue-600",
            bgColor: "bg-blue-50",
            borderColor: "border-l-blue-500",
            tasks: [
                {
                    id: "moveInRequests",
                    label: "Move-In request pendings",
                    count: tasks.tenantActions.moveInRequests,
                    route: "/requests"
                },
                {
                    id: "moveInVerification",
                    label: "Move-In verification pendings",
                    count: tasks.tenantActions.moveInVerification,
                    route: "/reserve/tenants"
                },
                {
                    id: "moveOutRequests",
                    label: "Move-Out request pending",
                    count: tasks.tenantActions.moveOutRequests,
                    route: "/requests"
                },
                {
                    id: "guestRequests",
                    label: "Guest request pending",
                    count: tasks.tenantActions.guestRequests,
                    route: "/requests"
                }
            ]
        },
        {
            title: "Finance Actions",
            icon: <FaMoneyBillWave className="w-4 h-4" />,
            color: "text-green-600",
            bgColor: "bg-green-50",
            borderColor: "border-l-green-500",
            tasks: [
                {
                    id: "landlordPayments",
                    label: "Landlord Payments Pending",
                    count: tasks.financeActions.landlordPayments,
                    route: "/landlord/pending-dues"
                },
                {
                    id: "refundRequests",
                    label: "Refund request pending",
                    count: tasks.financeActions.refundRequests,
                    route: "/requests"
                },
                {
                    id: "unpaidExpenses",
                    label: "Unpaid Expense",
                    count: tasks.financeActions.unpaidExpenses,
                    route: "/properties/recurring-exp"
                }
            ]
        },
        {
            title: "Operations Actions",
            icon: <FaWrench className="w-4 h-4" />,
            color: "text-purple-600",
            bgColor: "bg-purple-50",
            borderColor: "border-l-purple-500",
            tasks: [
                {
                    id: "unassignedComplaints",
                    label: "Unassigned Complaints",
                    count: tasks.operationsActions.unassignedComplaints,
                    route: "/complaints"
                },
                {
                    id: "agreementRenewals",
                    label: "Agreement Renewals Pending",
                    count: tasks.operationsActions.agreementRenewals,
                    route: "/tenants/agreement"
                },
                {
                    id: "unassignedPropComplaints",
                    label: "Unassigned Property Complaints",
                    count: tasks.operationsActions.unassignedPropComplaints,
                    route: "/complaints"
                },
                {
                    id: "unassignedLeads",
                    label: "Unassigned Leads",
                    count: tasks.operationsActions.unassignedLeads,
                    route: "/leads"
                }
            ]
        }
    ];

    return (
        <Container style={{ height: `${height}px` }}>
            <Header>
                <h1 className="text-white text-lg font-medium">Pending Task</h1>
                <button onClick={onClose}>
                    <MdClose color="white" size={24} />
                </button>
            </Header>

            <ContentContainer>
                <div className="p-4 space-y-6 bg-gray-50">
                    {taskSections.map((section, index) => (
                        <TaskCard key={index}>
                            {/* Header */}
                            <TaskHeader>
                                <div className="flex items-center gap-2">
                                    <div className={`p-2 rounded-md ${section.bgColor}`}>
                                        <div className={section.color}>{section.icon}</div>
                                    </div>
                                    <p className="font-medium text-gray-800 text-base">{section.title}</p>
                                </div>

                                <TaskBadge>
                                    {section.tasks.reduce((a, b) => a + b.count, 0)} Pending
                                </TaskBadge>
                            </TaskHeader>

                            <Divider />

                            {/* Content */}
                            <TaskGrid>
                                {section.tasks.map(task => (
                                    <TaskItem key={task.id} className={section.borderColor}>
                                        <TaskRow
                                            disabled={task.count === 0}
                                            onClick={() =>
                                                handleTaskClick(task.id, task.count, task.route)
                                            }
                                        >
                                            <FlexCol>
                                                <Label>{task.label}</Label>
                                            </FlexCol>

                                            <TaskRight>
                                                <TaskCount active={task.count > 0}>
                                                    {task.count}
                                                </TaskCount>

                                                <ArrowIcon active={task.count > 0}>
                                                    <MdChevronRight size={18} />
                                                </ArrowIcon>
                                            </TaskRight>
                                        </TaskRow>
                                    </TaskItem>
                                ))}
                            </TaskGrid>
                        </TaskCard>
                    ))}
                </div>
            </ContentContainer>
        </Container>
    );
};

const Container = tw.div`bg-white w-full h-full flex flex-col overflow-hidden`;
const Header = tw.div`flex items-center justify-between bg-[#26A69A] py-4 px-4 flex-shrink-0`;
const ContentContainer = tw.div`flex-1 overflow-y-auto max-h-[calc(100vh-100px)]`;
const TaskCard = tw.div`bg-white border border-gray-200 rounded-lg p-4 shadow-sm`;
const TaskHeader = tw.div`flex items-center justify-between`;
const TaskBadge = tw.span`text-xs px-3 py-1 rounded-full bg-primary-color/10 text-primary-color font-medium`;
const TaskGrid = tw.div`flex flex-col`;
const TaskItem = tw.div`hover:bg-gray-50 transition-colors duration-150`;
const TaskRow = tw.div`flex items-center justify-between py-2 cursor-pointer min-h-[44px]`;
const TaskCount = tw.span`min-w-[24px] text-right text-sm font-medium  ${({ active }) => active ? "text-red-600" : "text-gray-400"}`;
const Divider = tw.div`h-px bg-gray-200 my-3`;
const Label = tw.label`text-sm text-gray-800 font-medium line-clamp-1`;
const FlexCol = tw.div`flex flex-col gap-0.5 flex-1`;
const TaskRight = tw.div`flex items-center gap-2`;
const ArrowIcon = tw.span`transition-all ${({ active }) => active ? "text-gray-400" : "text-gray-300"}`;

export default PendingTasksModal;